/**
 * Recursively decode JSON-encoded strings.
 *
 * Sync log payloads come back from the backend in a double-encoded shape:
 * the outer body is a JSON string, and inside it, fields like `payload`
 * are themselves JSON-encoded strings. Naively rendering the value shows
 * raw backslashes and quotes around the inner JSON.
 *
 * This walks any value:
 *   * If a string looks like a JSON object/array (starts with `{` or `[`),
 *     try to parse it; on success, recurse into the parsed result.
 *   * For arrays / objects, recurse into each element.
 *   * Anything else (number, boolean, plain string, null) is returned
 *     verbatim.
 *
 * `JSON.parse` failures are silently swallowed and the original string is
 * returned — defensive in case a field happens to start with `{` but
 * isn't actually JSON.
 *
 * A `depth` guard prevents pathological inputs from running away — a
 * string that parses to another string that parses to another string,
 * etc. 8 levels is well beyond any real payload nesting we expect.
 */
export const deepParseJson = (value, depth = 0) => {
  if (depth > 8) return value;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return value;
    const first = trimmed[0];
    if (first !== '{' && first !== '[') return value;
    try {
      const parsed = JSON.parse(trimmed);
      return deepParseJson(parsed, depth + 1);
    } catch {
      return value;
    }
  }

  if (Array.isArray(value)) {
    return value.map((v) => deepParseJson(v, depth + 1));
  }

  if (value && typeof value === 'object') {
    const out = {};
    for (const [k, v] of Object.entries(value)) {
      out[k] = deepParseJson(v, depth + 1);
    }
    return out;
  }

  return value;
};

/**
 * Pretty-print a value as JSON, recursively decoding any nested
 * JSON-encoded strings first. Returns `null` for empty inputs so callers
 * can render a placeholder.
 */
export const formatJsonForDisplay = (value) => {
  if (value == null || value === '') return null;
  const parsed = deepParseJson(value);
  if (parsed == null) return null;
  if (typeof parsed === 'object' && !Array.isArray(parsed) && Object.keys(parsed).length === 0) {
    return null;
  }
  return JSON.stringify(parsed, null, 2);
};
